import { FormattedMessage } from 'react-intl';
import { SearchHookReturn } from './hooks/SearchHook';
import {
  ALL_SEARCH_CATEGORIES,
  SearchCategory,
  WP_HouseTerm,
} from '../../model/models';
import CategoryResults from './CategoryResults';
import { DirectoryFilterObject, WP_Tag } from '../../model/tags/tags';
import { Filter } from '../filters/Filter';
import { useFilterParents } from '../../hooks/FilterHooks';
import { AppContext } from '../../state/AppContextProvider';
import { useContext, useEffect, useState } from 'react';
import { useLogger } from '../../services/LogService';

interface SearchResultsProps {
  keywords: string;
  category: SearchCategory;
  getResults: (category: SearchCategory) => SearchHookReturn;
  isSearch: boolean;
  isSearching: boolean;
  more?: (
    category: SearchCategory,
    maxMore?: number
  ) => Promise<void> | undefined;
  filter?: DirectoryFilterObject;
  hasMore?: (category: SearchCategory) => boolean;
  search: any;
  resetFilter?: () => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  keywords,
  category,
  getResults,
  isSearch,
  isSearching,
  more = undefined,
  search,
  resetFilter,
}: SearchResultsProps) => {
  const byCategory =
    category === 'all'
      ? ALL_SEARCH_CATEGORIES.map((category) => {
          const results = getResults(category);
          return {
            category: category,
            items: results.items.slice(0, 3),
            loading: results.loading,
            hasMore: results.items.length > 3 || results.hasMore,
          };
        })
      : [
          {
            category: category,
            items: getResults(category).items,
            loading: getResults(category).loading,
            hasMore: getResults(category).hasMore,
          },
        ];

  const noResults =
    isSearch && byCategory.every((it) => !it.loading && it.items.length === 0);

  const categoryResults = getResults(category);

  const { state } = useContext(AppContext);

  const logger = useLogger('CategoryResultSearch');

  const parentTagsFiltered = state.parentTags.filter((tag) =>
    ['establishment', 'study_year', 'discipline'].includes(tag.slug)
  );

  const usersResults = getResults('users');

  const [triggerSearch, setTriggerSearch] = useState<boolean>(false);

  useEffect(() => {
    if (triggerSearch) {
      setTriggerSearch(false);
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSearch]);

  const onChange = (selectedSubTags) => {
    logger.info('Applying filters');

    // Reset before anything to be sure
    if (resetFilter) {
      resetFilter();
    }

    // Create list of tags and parents name to make the search
    let tagsList: {
      [tagType: string]: WP_Tag | WP_HouseTerm | string | null;
    } = {};
    selectedSubTags.map((filterCategory) => {
      const tagParentName = state.parentTags.find((parentTag) => {
        return parentTag.id === filterCategory.parentTagId;
      });

      return filterCategory.tags.map((tag) => {
        if (tagParentName !== undefined) {
          return (tagsList[tagParentName.slug] = tag);
        } else {
          return tagsList;
        }
      });
    });

    if (usersResults.changeFilter !== undefined) {
      usersResults.changeFilter(tagsList);
    }

    setTriggerSearch(true);
  };

  const onResetFilter = () => {
    logger.info('Reseting filters');
    // Reset for the filter component
    onReset();
    if (usersResults.resetFilter) {
      // Reset for the SearchHook
      usersResults.resetFilter();
    }
    // Call the search Query to make it work
    search();
  };

  const {
    selectParentTag,
    setSelectedParentTag,
    selectedParentTagId,
    childrenTags,
    selectSubTag,
    isSubTagSelected,
    selectedSubTags,
    setAllSelected,
    isFiltered,
    onReset,
    showModal,
    openModal,
  } = useFilterParents(parentTagsFiltered, onChange, false);

  const isFilter = isFiltered();

  const categoryResultsComponent = byCategory.map(
    ({ category, items, loading, hasMore }) => (
      <CategoryResults
        key={category}
        category={category}
        items={items}
        more={more}
        hasMore={hasMore}
        isSearch={isSearch}
        isFilter={isFilter}
        openModal={openModal}
      />
    )
  );

  return (
    <>
      <div
        className={`w-full text-xs text-center ${
          isSearching ? '' : 'invisible'
        }`}
      >
        <FormattedMessage id="Searching" />
      </div>
      {noResults && (
        <div className="w-full flex flex-col justify-center items-center">
          <span className="mb-4">
            <FormattedMessage id="No Results" />
          </span>
        </div>
      )}
      {isSearch && categoryResultsComponent && (
        <div className="w-full mb-20 md:mb-24 md:mt-6 flex flex-col gap-y-6">
          {categoryResults && categoryResults.filter && (
            <Filter
              className="directory"
              multipleSelect={false}
              categories={parentTagsFiltered}
              selectParentTag={selectParentTag}
              setSelectedParentTag={setSelectedParentTag}
              selectedParentTagId={selectedParentTagId}
              childrenTags={childrenTags}
              selectSubTag={selectSubTag}
              isSubTagSelected={isSubTagSelected}
              selectedSubTags={selectedSubTags}
              setAllSelected={setAllSelected}
              onResetFilter={onResetFilter}
              isFiltered={isFiltered}
              backUrl={'/search/users'}
              showModal={showModal}
              openModal={openModal}
            />
          )}
          {categoryResultsComponent}
        </div>
      )}
    </>
  );
};

export default SearchResults;
